import { Spacing } from '@persona-id/design-tokens';
import Fuse from 'fuse.js';
import { useCallback, useMemo, useState } from 'react';
import { DragDropContext, DropResult } from 'react-beautiful-dnd';
import styled from 'styled-components';

import {
  FIELD_SCHEMA_EDITOR_SUPPORTED_FIELD_TYPE_OPTIONS_DISPLAY,
  FieldSchemaEditorSupportedTypes,
  FocusReason,
} from 'dashboard/components-lib/FieldSchemaEditor/constants';
import EmptyState from 'dashboard/components-lib/FieldSchemaEditor/EmptyState';
import FieldRow from 'dashboard/components-lib/FieldSchemaEditor/FieldRow';
import { Column } from 'dashboard/components-lib/FieldSchemaEditor/table';
import {
  fieldTypeFilterOptions,
  moveFieldWithinSchema,
} from 'dashboard/components-lib/FieldSchemaEditor/utils';
import MultiSelect, { useLabelTextMapper } from 'dashboard/components-lib/MultiSelect';
import SearchInput from 'dashboard/components-lib/SearchInput';
import { Identifier } from 'dashboard/models/identifier';
import { Z_INDICES } from 'dashboard/styles/variables';
import Spacer from 'design-system/components/Spacer';
import Sticker from 'design-system/components/Sticker';
import Table, { TableHead, TableHeaderCell, TableRow } from 'design-system/components/Table';
import { FieldSchemaType } from 'lib/fields';
import useToggle from 'lib/hooks/useToggle';

const TitleContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 0.8fr;
  grid-gap: ${Spacing[3]};
  width: 100%;
  align-items: center;
  margin-bottom: ${Spacing[4]};
`;

const StickyTableHead = styled(TableHead).attrs(() => ({ sticky: true }))`
  z-index: ${Z_INDICES.sticky};
`;

export const convertDraggableId = (draggableId?: string, schemaKey?: string) => {
  if (!draggableId && !schemaKey) {
    return '';
  }
  if (draggableId) {
    return draggableId.replace(/-draggable$/, '');
  }
  if (schemaKey) {
    return `${schemaKey}-draggable`;
  }

  return '';
};

export const convertDroppableId = (droppableId?: string, schemaKey?: string) => {
  if (!droppableId && !schemaKey) {
    return '';
  }

  if (droppableId) {
    return droppableId.replace(/-droppable$/, '');
  }
  if (schemaKey) {
    return `${schemaKey}-droppable`;
  }

  return '';
};

const LabelColumn = styled.div`
  display: -webkit-box;
  overflow: hidden;
  white-space: normal;
  text-overflow: clip;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  line-height: 20px;
`;

const defaultColumns: Column[] = [
  {
    header: 'Label',
    generateContent: ({ field }) => <LabelColumn>{field.label}</LabelColumn>,
  },
  {
    header: 'Type',
    width: '150px',
    generateContent: ({ field, highlighted }) => {
      if (!field.type) {
        return;
      }
      // TODO(zixiao): show object type name for relation type description
      return (
        <>
          <Sticker disabled={!highlighted}>
            {FIELD_SCHEMA_EDITOR_SUPPORTED_FIELD_TYPE_OPTIONS_DISPLAY[field.type]}
          </Sticker>
          {field.type === FieldSchemaType.Array && field.config['item-schema'] && (
            <>
              <Spacer width={0.5} />
              of
              <Spacer width={0.5} />
              <Sticker disabled={!highlighted}>
                {
                  FIELD_SCHEMA_EDITOR_SUPPORTED_FIELD_TYPE_OPTIONS_DISPLAY[
                    field.config['item-schema'].type
                  ]
                }
              </Sticker>
            </>
          )}
        </>
      );
    },
  },
];

export interface FieldSchemaEditorTableProps {
  inputSchema: FieldSchemaEditorSupportedTypes[];
  inputIdentifiers?: Identifier[];
  productIcon: React.ReactElement;
  nullState: React.ReactNode;
  customColumns?: Column[];
  focusedKey?: string;
  erroredKeys?: string[];
  uneditableKeys?: string[];
  preexistingKeys?: string[];
  editable: boolean;
  hideTableHeader?: boolean;
  fieldTypes?: FieldSchemaType[];
  identifierConfigEnabled?: boolean;
  onFocusSchema: (path: string, focusReason: FocusReason) => void;
  onUpdateSchema: (fieldSchema: FieldSchemaEditorSupportedTypes[], autoValidate?: boolean) => void;
  updateIdentifiers?: (identifierConfig: Identifier[]) => void;
}

const FieldSchemaEditorTable: React.FC<FieldSchemaEditorTableProps> = ({
  inputSchema,
  inputIdentifiers = [],
  productIcon,
  nullState,
  customColumns = [],
  focusedKey,
  erroredKeys = [],
  uneditableKeys = [],
  // In most cases, already saved (preexisting) keys are the same as the uneditable keys since preexisting
  // keys should not be changed. However, for account system keys, preexisting keys and uneditable keys are
  // not the same set of keys (system fields are uneditable but may not be preexisting), and so we have to
  // separate them out.
  preexistingKeys = uneditableKeys,
  editable,
  hideTableHeader = false,
  identifierConfigEnabled = false,
  fieldTypes = Object.values(FieldSchemaType),
  onFocusSchema,
  onUpdateSchema,
  updateIdentifiers,
}) => {
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [typeFilters, setTypeFilters] = useState<string[]>([]);

  const fuse = useMemo(
    () =>
      new Fuse(inputSchema, {
        threshold: 0.0,
        shouldSort: true,
        ignoreLocation: true,
        minMatchCharLength: 1,
        keys: [
          { name: 'label', weight: 0.65 },
          { name: 'key', weight: 0.35 },
        ],
      }),
    [inputSchema],
  );

  const typeFilterOptions = fieldTypeFilterOptions(fieldTypes);
  const typeFilterLabelTextMapper = useLabelTextMapper(typeFilterOptions, 'types', {
    emptyIsAll: true,
  });

  const searchResults: FieldSchemaEditorSupportedTypes[] = useMemo(() => {
    const fieldSchemaToRender =
      searchTerm !== '' ? fuse.search(searchTerm).map(({ item }) => item) : inputSchema;
    return fieldSchemaToRender.filter(
      ({ type }) => typeFilters.length === 0 || typeFilters.includes(type),
    );
  }, [fuse, inputSchema, searchTerm, typeFilters]);

  const columns = useMemo<Column[]>(() => {
    const optionalColumns = [];

    if (identifierConfigEnabled) {
      optionalColumns.push({
        header: 'Identifier',
        width: '80px',
        generateContent: ({ field }) => {
          const isIdentifier = inputIdentifiers?.some((identifer) => identifer.key === field.key);

          return (
            identifierConfigEnabled && (
              <LabelColumn>
                {field.type !== FieldSchemaType.String ? '-' : isIdentifier ? 'Yes' : 'No'}
              </LabelColumn>
            )
          );
        },
      } as Column);
    }
    return [...defaultColumns, ...optionalColumns, ...customColumns];
  }, [customColumns, identifierConfigEnabled, inputIdentifiers]);

  const moveField = useCallback(
    (event: DropResult) => {
      if (!editable) {
        return;
      }
      if (event.reason !== 'DROP') {
        return;
      }
      const sourceKey = convertDraggableId(event.draggableId, '');
      const destinationKey = convertDroppableId(event.destination?.droppableId, '');
      const { updatedSchemas } = moveFieldWithinSchema(inputSchema, sourceKey, destinationKey);
      onUpdateSchema(updatedSchemas, true);
    },
    [editable, inputSchema, onUpdateSchema],
  );

  const [isDragging, { enable: setIsDragging, disable: setIsNotDragging }] = useToggle(false);
  const hasUsedFilters = searchTerm !== '' || typeFilters.length > 0;

  return (
    <DragDropContext
      onDragStart={() => {
        setIsDragging();
      }}
      onDragEnd={(e) => {
        moveField(e);
        setIsNotDragging();
      }}
    >
      <TitleContainer>
        <SearchInput
          size='md'
          title={null}
          value={searchTerm}
          handleInput={setSearchTerm}
          placeholder='Search for a field'
        />
        <MultiSelect
          size='md'
          value={typeFilters}
          selectOptionValues={typeFilterOptions}
          labelTextMapper={typeFilterLabelTextMapper}
          onChange={setTypeFilters}
        />
      </TitleContainer>
      <Table style={{ tableLayout: 'fixed' }}>
        {hideTableHeader ? null : (
          <StickyTableHead>
            <TableRow>
              <TableHeaderCell>Key</TableHeaderCell>
              {columns.map((column, columnIndex) => {
                return (
                  <TableHeaderCell
                    key={`${columnIndex}-${column.header}-header`}
                    style={{ width: column.width ?? 'auto' }}
                  >
                    {column.header}
                  </TableHeaderCell>
                );
              })}
            </TableRow>
          </StickyTableHead>
        )}
        {searchResults.map((fieldSchema, index: number) => {
          return (
            <FieldRow
              key={`field-${fieldSchema.key}-${index}`}
              prefix={fieldSchema.key ? fieldSchema.key : `${index}`}
              inputSchema={fieldSchema}
              customColumns={columns}
              focusedKey={focusedKey}
              erroredKeys={erroredKeys}
              nestLevel={0}
              uneditableKeys={uneditableKeys}
              preexistingKeys={preexistingKeys}
              hasEditPermission={editable}
              isDragging={isDragging}
              onInsertSibling={(sibling, identifierConfig) => {
                const siblingKey = sibling.key ? sibling.key : `${index + 1}`;
                onFocusSchema(siblingKey, 'add');
                inputSchema = [
                  ...inputSchema.slice(0, index + 1),
                  sibling,
                  ...inputSchema.slice(index + 1),
                ];
                onUpdateSchema(inputSchema);
                if (identifierConfig && updateIdentifiers) {
                  updateIdentifiers([...inputIdentifiers, identifierConfig]);
                }
                return siblingKey;
              }}
              onUpdateFieldSchema={(updatedSelf) => {
                inputSchema = [
                  ...inputSchema.slice(0, index),
                  updatedSelf,
                  ...inputSchema.slice(index + 1),
                ];
                onUpdateSchema(inputSchema);
              }}
              onFocusFieldSchema={onFocusSchema}
            />
          );
        })}
      </Table>
      {searchResults.length === 0 && (
        <EmptyState productIcon={productIcon}>
          {hasUsedFilters ? 'No fields found. Please try another search.' : nullState}
        </EmptyState>
      )}
    </DragDropContext>
  );
};

export default FieldSchemaEditorTable;
